import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import {
  AcceptIcon,
  Button,
  CloseIcon,
  Dialog,
  SettingsIcon,
  ShiftActivityIcon,
} from "@fluentui/react-northstar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import "./styles.scss";

// Helper function to add SAS token to image URLs
const addSasTokenToImageUrl = (imageUrl) => {
  if (!imageUrl || !process.env.REACT_APP_AZURE_SAS_TOKEN) return imageUrl;
  
  const trimmedUrl = imageUrl.trim();
  const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
  
  if (trimmedUrl.includes('?')) {
    return `${trimmedUrl}&${sasToken}`;
  }
  return `${trimmedUrl}?${sasToken}`;
};

// Props: trustRequest, onOpenConfig, onApprove, onReject, showActions, isGroupRequest, isApprovingRequest, isRejectingRequest, onRequestDeleted, isMobile, isMediumScreen
const TrustRequestCard = ({
  trustRequest,
  onOpenConfig,
  onApprove,
  onReject,
  showActions = true,
  isGroupRequest = false,
  isApprovingRequest = {},
  isRejectingRequest = {},
  onRequestDeleted,
  isMobile = false,
  isMediumScreen = false,
}) => {
  const { t } = useTranslation();
  const MainTranslation = t("trustedNetwork").trustRequests;
  const translation = MainTranslation.trustRequestCard;

  const {
    isApprovingTrustRequest,
    isRejectingTrustRequest,
    cancelTrustRequests,
    user,
  } = useTrustedNetwork();

  const isApproving = isGroupRequest
    ? isApprovingRequest[trustRequest.id]
    : isApprovingTrustRequest[trustRequest.id];
  const isRejecting = isGroupRequest
    ? isRejectingRequest[trustRequest.id]
    : isRejectingTrustRequest[trustRequest.id];

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const renderRequestContent = () => {
    if (isGroupRequest) {
      const groupNetwork = trustRequest.groupNetwork || {};

      return (
        <>
          <div className="header-section">
            <div className="header-content">
              <div className="header-left">
                <div className="header-icon">
                  {trustRequest.inviterSchoolLogo ? (
                    <img 
                      src={addSasTokenToImageUrl(trustRequest.inviterSchoolLogo)} 
                      alt="School Logo" 
                      className="school-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="fallback-icon" style={{display: trustRequest.inviterSchoolLogo ? 'none' : 'flex'}}>
                    <TeamOutlined />
                  </div>
                </div>
                <div className="header-text">
                  <h3 className="header-title">
                    {groupNetwork.name || "Unnamed Group"}
                  </h3>
                  <p className="header-subtitle">
                    {translation.from}:{" "}
                    {trustRequest.inviterSchoolName ||
                      translation.unknownSchool}
                  </p>
                  {trustRequest.inviterSchoolEmail && (
                    <p className="school-email">
                      {trustRequest.inviterSchoolEmail}
                    </p>
                  )}
                </div>
              </div>
              <div className="header-right">
                <div className="date-info">
                  <span className="date-label">{translation.receivedAt}</span>
                  <span className="date-value">
                    {new Date(trustRequest.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="content-section">
            {groupNetwork.description && (
              <div className="content-message">
                <div className="message-content">
                  {groupNetwork.description}
                </div>
              </div>
            )}
            {trustRequest.message && (
              <div className="content-message">
                <div className="message-content">{trustRequest.message}</div>
              </div>
            )}
          </div>
        </>
      );
    }

    const schoolLogo = showActions 
      ? trustRequest.requesterSchoolLogo 
      : trustRequest.targetSchoolLogo;
    const schoolName = showActions
      ? trustRequest.requesterSchoolName || "Unknown School"
      : trustRequest.targetSchoolName || "Unknown School";
    const schoolEmail = showActions
      ? trustRequest.requesterSchoolEmail
      : trustRequest.targetSchoolEmail;

    return (
      <>
        <div className="header-section">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon">
                {schoolLogo ? (
                  <img 
                    src={addSasTokenToImageUrl(schoolLogo)} 
                    alt="School Logo" 
                    className="school-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="fallback-icon" style={{display: schoolLogo ? 'none' : 'flex'}}>
                  <UserOutlined />
                </div>
              </div>
              <div className="header-text">
                <h3 className="header-title">
                  {schoolName}
                </h3>
                <p className="header-subtitle">
                  {showActions ? "Trust Request" : "Sent Request"}
                </p>
                {schoolEmail && (
                  <p className="school-email">
                    {schoolEmail}
                  </p>
                )}
              </div>
            </div>
            <div className="header-right">
              <div className="date-info">
                <span className="date-label">
                  {showActions ? translation.receivedAt : translation.sentAt}
                </span>
                <span className="date-value">
                  {new Date(trustRequest.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="content-section">
          {/* Content section intentionally left minimal as requested */}
        </div>
      </>
    );
  };

  return (
    <div className="trust-request-card">
      <div className="card-body">
        {renderRequestContent()}

        <div className="actions-section">
          {showActions ? (
            <>
              <Button
                icon={<CloseIcon />}
                content={translation.reject}
                title={translation.rejectRequest}
                onClick={onReject}
                disabled={isApproving || isRejecting}
                loading={isRejecting}
                className="action-button reject-button"
              />
              <Button
                icon={<AcceptIcon />}
                content={translation.accept}
                title={translation.acceptRequest}
                onClick={onApprove}
                loading={isApproving}
                disabled={isApproving || isRejecting}
                className="action-button accept-button"
              />
              <Button
                title={
                  isGroupRequest
                    ? translation.configureGroupSettings
                    : translation.configureTrustedNetwork
                }
                icon={<SettingsIcon />}
                content={translation.configure}
                disabled={isApproving || isRejecting}
                onClick={onOpenConfig}
                className="action-button config-button"
              />
            </>
          ) : (
            <>
              <div className="pending-status">
                <ShiftActivityIcon className="pending-icon" />
                <span className="pending-text">{translation.pending}</span>
              </div>
              <Button
                icon={<CloseIcon />}
                title={translation.deleteRequest}
                content={translation.delete}
                loading={isDeleting}
                onClick={() => setShowDeleteDialog(true)}
                className="action-button delete-button"
              />
              <Dialog
                open={showDeleteDialog}
                onCancel={() => setShowDeleteDialog(false)}
                header={translation.deleteRequest}
                content={translation.deleteDisclaimer}
                confirmButton={{
                  content: translation.delete,
                  primary: true,
                  danger: true,
                  loading: isDeleting,
                  onClick: async () => {
                    setIsDeleting(true);
                    await cancelTrustRequests(trustRequest.id);
                    setIsDeleting(false);
                    setShowDeleteDialog(false);
                    if (onRequestDeleted) onRequestDeleted();
                  },
                }}
                cancelButton={{
                  content: translation.cancel,
                  onClick: () => setShowDeleteDialog(false),
                }}
                className="delete-confirmation-dialog"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustRequestCard;
